class StickyCollectionNav extends HTMLElement {
  constructor() {
    super();
    this.nav = null;
    this.heroSection = null;
    this.sectionHeader = null;
    this.navLinks = null;
    this.sections = [];
    this.ticking = false;
    this.scrollHandlerDisabled = false;
  }

  connectedCallback() {
    this.nav = this.querySelector('nav');
    this.heroSection = document.querySelector('.collection-hero__text-wrapper');
    this.sectionHeader = document.querySelector('.section-header');
    this.navLinks = this.nav ? this.nav.querySelectorAll('a[href^="#"]') : [];

    if (!this.nav || !this.heroSection || !this.sectionHeader || !this.navLinks) return;

    this.sections = Array.from(this.navLinks)
      .map((link) => {
        return document.getElementById(link.getAttribute('href').substring(1));
      })
      .filter(Boolean);

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            this.nav.classList.add('is-sticky');
          } else {
            this.nav.classList.remove('is-sticky');
          }
        });
      },
      {
        root: null,
        threshold: 0.15,
        rootMargin: '-1px 0px 0px 0px',
      }
    );
    this.observer.observe(this.heroSection);

    // Scroll handler with throttling
    this.onScrollHandler = () => {
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.updateNavOffset();
          this.updateActiveSection();
          this.ticking = false;
        });
        this.ticking = true;
      }
    };
    window.addEventListener('scroll', this.onScrollHandler);
    this.updateNavOffset();

    this.navLinks.forEach((link) => {
      link.addEventListener('click', (e) => this.handleNavClick(e));
    });
  }

  updateActiveSection() {
    if (this.scrollHandlerDisabled) return;
    const headerHeight =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const navHeight = this.nav.offsetHeight;
    const scrollThreshold = headerHeight + navHeight + 30;

    let closestSection = null;
    let closestDistance = Infinity;

    this.sections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      const distance = rect.top - scrollThreshold;

      if (distance < 0 && Math.abs(distance) < closestDistance) {
        closestDistance = Math.abs(distance);
        closestSection = section;
      }
    });

    this.navLinks.forEach((link) => {
      const isActive = closestSection && link.getAttribute('href') === `#${closestSection.id}`;
      link.classList.toggle('active', isActive);

      if (isActive && window.innerWidth < 990 && this.nav.classList.contains('is-sticky')) {
        // Get current scroll position
        const currentScroll = this.nav.scrollLeft;

        // Get link position relative to navigation container
        const linkRect = link.getBoundingClientRect();
        const navRect = this.nav.getBoundingClientRect();

        // Calculate required scroll position with 20px offset
        const targetScroll = currentScroll + (linkRect.left - navRect.left) - 20;

        // Apply boundaries
        const maxScroll = this.nav.scrollWidth - this.nav.clientWidth;
        const finalPosition = Math.max(0, Math.min(targetScroll, maxScroll));

        if (Math.abs(finalPosition - currentScroll) > 5) {
          this.nav.scrollTo({
            left: finalPosition,
            behavior: 'smooth',
          });
        }
      }
    });
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  updateNavOffset() {
    if (this.sectionHeader.classList.contains('shopify-section-header-hidden')) {
      this.nav.style.top = '0';
    } else {
      this.nav.style.top = 'calc(var(--header-height))';
    }
  }

  handleNavClick(e) {
    e.preventDefault();
    const targetId = e.currentTarget.getAttribute('href').substring(1);
    const targetElement = document.getElementById(targetId);
    if (!targetElement) return;

    this.scrollHandlerDisabled = true;

    const navHeight = this.nav.offsetHeight || 0;
    const headerHeight =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const offset = navHeight + headerHeight;
    const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - offset;

    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth',
    });

    this.navLinks.forEach((link) => link.classList.remove('active'));
    e.currentTarget.classList.add('active');

    if (window.innerWidth < 990 && this.nav.classList.contains('is-sticky')) {
      const currentScroll = this.nav.scrollLeft;
      const linkRect = e.currentTarget.getBoundingClientRect();
      const navRect = this.nav.getBoundingClientRect();
      const targetScroll = currentScroll + (linkRect.left - navRect.left) - 20;

      const maxScroll = this.nav.scrollWidth - this.nav.clientWidth;
      const finalPosition = Math.max(0, Math.min(targetScroll, maxScroll));

      this.nav.scrollTo({
        left: finalPosition,
        behavior: 'smooth',
      });
    }

    setTimeout(() => {
      this.scrollHandlerDisabled = false;
    }, 1000);
  }
}

customElements.define('sticky-collection-nav', StickyCollectionNav);
